import { Checkbox, Drawer, Input, message, Select, Space, Image, Empty, Timeline, Modal, Alert, Col, Descriptions, Row, Tag, Radio, Segmented } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'antd';
import { DeleteOutlined, EditOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import ImageFileUpload from '../Inputs/ImageFileUpload';
import { UploadChangeParam } from 'antd/es/upload';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ReactTimeago from 'react-timeago';
import { Typography } from 'antd';
import { diff, flattenChangeset } from 'json-diff-ts';
import { BASE_PATH } from '../../..';
import { AssetApprovalStatus, AssetApprovalStatusLabels, AssetApprovalStatusOptions, AssetSourceType } from '../../../models/enums';
import ApproveMaterial from './ApproveMaterial';
import { UserSelector } from '../Inputs/UserSelector';
import { AssetSelector } from '../Inputs/AssetSelector';
import { AssetApprovalHistoryDTO, MaterialsService, MaterialViewDTO } from '../../../backendapi/requests';
import { useClientsServiceGetBackendClients, useMaterialsServiceGetBackendMaterialsQAimages, useMaterialsServicePutBackendMaterials } from '../../../backendapi/queries';

const { Text, Title } = Typography;

const EditMaterial: React.FC<{ materialId?: number, isOpen: boolean, onClose: () => void }> = ({ materialId, isOpen, onClose }) => {
    const {data: clients} = useClientsServiceGetBackendClients({}, undefined, {enabled: isOpen});
    const {data: qaImages} = useMaterialsServiceGetBackendMaterialsQAimages({key: materialId ?? 0}, undefined, {enabled: isOpen && materialId !== undefined});
    const {mutate, isPending, isSuccess, isError, error } = useMaterialsServicePutBackendMaterials();
    const [approvalOpen, setApprovalOpen] = useState(false);

    const [form] = Form.useForm<MaterialViewDTO>();
    const [messageApi, contextHolder] = message.useMessage();

    const [materialImage, setMaterialImage] = useState("");
    const [material, setMaterial] = useState<MaterialViewDTO | undefined>(undefined);

    const clientId = Form.useWatch('clientId', form);
    const filename = Form.useWatch('filename', form);
    const source = Form.useWatch('sourceType', form);
    const sourcePath = Form.useWatch('sourcePath', form);
    const title = Form.useWatch('title', form);

    const assetFilePath = useMemo(() => {
        if(clients){
            let client = clients.value.find(e => e.id === clientId);
            if (client) {
                return client.maxMaterialsPath + "\\" + filename;
            }
        }
        return "";
    }, [clientId, clients, filename]);

    const onSubmit = () => {
        form.submit();
    }
    
    const getThumbnailPath = useCallback(() => {
        return `${BASE_PATH}/material/${materialId}/thumbnail?rnd=` + Math.floor(Math.random() * 999999999)
    }, [materialId])

    const onUpload = (info: UploadChangeParam) => {
        if (materialId) {
            setMaterialImage(getThumbnailPath())
        }
    }

    const onFinish = () => {
        if (materialId) {
            const values = form.getFieldsValue(true) as MaterialViewDTO;
            mutate({key: materialId, requestBody: values});
        }
    };

    useEffect(() => {
        if(isSuccess){
            messageApi.success("Material updated");
        }
    }, [isSuccess, messageApi]);

    useEffect(() => {
        if(isError){
            messageApi.error(error?.toString());
        }
    }, [error, isError, isSuccess, messageApi]);

    useEffect(() => {
        if (materialId && isOpen) {
            form.resetFields();

            MaterialsService.getBackendMaterials1(materialId).then(material => {
                setMaterial(material);
                setMaterialImage(getThumbnailPath());
                form.setFieldsValue({...material, metadata: [...material.metadata].sort((a, b) => ((a.locked ? 1 : 0) - (b.locked ? 1 : 0)) || a.name.localeCompare(b.name)) });
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialId, isOpen]);

    return (
        <>
            {contextHolder}

            <Drawer
                title={`Edit ${title}`}
                width={720}
                onClose={onClose}
                open={isOpen}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={isPending}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    name="basic"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}>

                    <Form.Item rules={[{ required: true }]} name='clientId' label="Client">
                        <Select options={clients?.value.map(e => ({ label: e.name, value: e.id }))} />
                    </Form.Item>

                    <Form.Item label="Thumbnail">
                        <Space direction='vertical'>
                            <ImageFileUpload title="Upload thumbnail" action={`${BASE_PATH}/material/${material?.id}/image`} onUpload={onUpload} />
                            <Image preview={false} width={300} src={materialImage} />
                        </Space>
                    </Form.Item>

                    <Form.Item label="QA Images">
                        <Space wrap>
                            {qaImages?.map(img => <Image key={img} placeholder="" width={150} src={BASE_PATH + img} />)}
                            {qaImages?.length === 0 && <Empty />}
                        </Space>
                    </Form.Item>

                    <Form.Item name="isEnabled" valuePropName="checked">
                        <Checkbox>Enabled</Checkbox>
                    </Form.Item>

                    <Form.Item name="approvalStatus" label="Status">
                            <Segmented vertical
                                options={[
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.Quote], value: AssetApprovalStatus.Quote, disabled: true },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.Pending], value: AssetApprovalStatus.Pending, disabled: true },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.InProduction], value: AssetApprovalStatus.InProduction },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.Commited], value: AssetApprovalStatus.Commited },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.New], value: AssetApprovalStatus.New },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.Rejected], value: AssetApprovalStatus.Rejected },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.RejectedButModified], value: AssetApprovalStatus.RejectedButModified, disabled: true },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.RejectedPendingModification], value: AssetApprovalStatus.RejectedPendingModification, disabled: true },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.Approved], value: AssetApprovalStatus.Approved },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.ApprovedButModified], value: AssetApprovalStatus.ApprovedButModified, disabled: true },
                                    { label: AssetApprovalStatusOptions[AssetApprovalStatus.ApprovedPendingModification], value: AssetApprovalStatus.ApprovedPendingModification, disabled: true },
                                ]}
                            />
                    </Form.Item>

                    <Form.Item label="History">
                        <Timeline items={[
                            {
                                color: 'blue',
                                children: (<>
                                    Material created <ReactTimeago date={material?.timestampCreate ?? ""} />
                                </>)
                            },
                            ...(material?.approvalHistory || []).map(e => ({
                                color: e.newStatus === AssetApprovalStatus.Approved ? 'green' : (e.newStatus === AssetApprovalStatus.Rejected ? 'red' : 'blue'),
                                children: (<>
                                    Status changed from <b>{AssetApprovalStatusOptions[e.oldStatus as AssetApprovalStatus]}</b> to <b>{AssetApprovalStatusOptions[e.newStatus as AssetApprovalStatus]}</b> {' '}
                                    <ReactTimeago date={e.timestamp} />
                                    <MaterialHistoryDetailsButton item={e} material={material} />
                                    {e.comment && <>
                                        <br />
                                        <Text code>{e.comment}</Text>
                                    </>}
                                </>)
                            }))]} />
                            <Button type='primary' onClick={() => setApprovalOpen(true)}>Start Approval</Button>
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='title' label="Title">
                        <Input />
                    </Form.Item>

                    <Form.Item name='producingArtistId' label="Artist">
                        <UserSelector />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='complexity' label="Complexity" >
                        <Radio.Group
                            options={[
                                { label: 'Simple', value: 0 },
                                { label: 'Complex', value: 5 },
                                { label: 'Very complex', value: 10 }
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                        />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='sourceType' label="Source" >
                        <Radio.Group
                            options={[
                                { label: 'Unknown', value: 0 },
                                { label: 'Existing CoD asset', value: 1 },
                                { label: 'Workflow', value: 2 },
                                { label: 'None', value: 3 },
                                { label: 'CAD File', value: 4 },
                                { label: 'Max File', value: 5 }
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                        />
                    </Form.Item>

                    {source === AssetSourceType.Workflow &&
                        <Form.Item label="Workflow asset">
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item name='sourcePath' style={{ flexGrow: 1 }} noStyle>
                                    <Input addonBefore="http://" />
                                </Form.Item>
                                <Button type="default" onClick={() => window.open(sourcePath ?? "")}>Open</Button>
                            </Space.Compact>
                        </Form.Item>}

                    {source === AssetSourceType.ExistingAsset &&
                        <Form.Item label="Source material" name="sourcePath">
                            <AssetSelector type='Material' />
                        </Form.Item>}

                    {(source === AssetSourceType.CADFile || source === AssetSourceType.MaxFile) &&
                        <Form.Item label="Source file folder">
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item name='sourcePath' style={{ flexGrow: 1 }} noStyle>
                                    <Input />
                                </Form.Item>
                                <CopyToClipboard text={sourcePath ?? ""}>
                                    <Button type="default">Copy path</Button>
                                </CopyToClipboard>
                            </Space.Compact>
                        </Form.Item>
                    }

                    <Form.Item name='notes' label="Notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item label="Filename">
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name='filename' style={{ flexGrow: 1 }} noStyle>
                                <Input />
                            </Form.Item>
                            <CopyToClipboard text={assetFilePath}>
                                <Button type="default">Copy full path</Button>
                            </CopyToClipboard>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='name' label="Name">
                        <Input />
                    </Form.Item>

                    <Form.Item name='tags' label="Tags">
                        <Select mode='tags' options={[]} />
                    </Form.Item>
                    
                    <Form.Item label='Metadata'>
                        {material?.timestampMetadataImport && <p>Metadata imported from DAM <ReactTimeago date={material?.timestampMetadataImport} /></p>}
                        <Form.List name="metadata">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                style={{width: 180}}
                                                name={[name, 'name']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="name" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'value']}
                                                style={{width: 360}}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="value" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            {form.getFieldValue(["metadata", name, "locked"]) !== true && <MinusCircleOutlined onClick={() => remove(name)} />}
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add metadata
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Drawer>

            <ApproveMaterial isOpen={approvalOpen} onClose={() => setApprovalOpen(false)} materials={material === undefined ? [] : [material]} hideEditor  />
        </>

    );
};


export const MaterialHistoryDetailsButton: React.FC<{ material?: MaterialViewDTO, item: AssetApprovalHistoryDTO }> = ({ material, item }) => {

    const [isOpen, setIsOpen] = useState(false);

    const showModal = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleOk = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        setIsOpen(false);
    }, []);

    const oldMaterial = useMemo(() => {
        if (item.assetData && isOpen) {
            return JSON.parse(item.assetData) as MaterialViewDTO;
        }
        return undefined;
    }, [isOpen, item.assetData]);

    const diffs = useMemo(() => {
        if (item.assetData && isOpen) {
            var oldData = JSON.parse(item.assetData) as MaterialViewDTO;
            let changeset = flattenChangeset(diff(oldData, material, { metadata: 'name' }));

            const ignoredProperties = ["$.approvalStatus", "$.timestampCreate"];
            changeset = changeset.filter(e => !ignoredProperties.includes(e.path));

            return changeset;
        }
        return undefined;
    }, [isOpen, item.assetData, material]);

    return (<>
        <Button size='small' type='link' onClick={showModal}>Details</Button>

        <Modal open={isOpen} onOk={handleOk} onCancel={handleCancel} width={1000}>
            <p>
                Status changed from <b>{AssetApprovalStatusLabels[item.oldStatus as AssetApprovalStatus]}</b> to <b>{AssetApprovalStatusLabels[item.newStatus as AssetApprovalStatus]}</b> at {item.timestamp} by {item.username ?? "API"}
            </p>
            {item.comment && <p>
                Comment: <br />
                {item.comment}
            </p>}

            <Row>
                <Col span={12}>
                    <Title level={2}>Images</Title>
                    {item.images.map(e => <Image src={BASE_PATH + e} />)}


                    <Title level={2}>Data</Title>

                    {oldMaterial &&
                        <Descriptions layout='horizontal' size='small' bordered column={1} items={[
                            {
                                label: 'Status',
                                children: AssetApprovalStatusLabels[(oldMaterial?.approvalStatus ?? 0) as AssetApprovalStatus]
                            },
                            {
                                label: 'Title',
                                children: oldMaterial?.title
                            },
                            {
                                label: 'Filename',
                                children: oldMaterial?.filename
                            },
                            {
                                label: 'Name',
                                children: oldMaterial?.name
                            },
                            {
                                label: 'Tags',
                                children: oldMaterial?.tags?.map(e => <Tag>{e}</Tag>)
                            },
                            {
                                label: 'Meta data',
                                children: <Descriptions size='small' colon layout='horizontal' column={1} items={oldMaterial?.metadata?.map(e => ({ label: e.name, children: e.value }))} />
                            },
                        ]} />
                    }


                </Col>
                <Col span={12}>
                    <Title>Diff with current data</Title>
                    {diffs?.map(e => <>
                            {e.type === "UPDATE" ? <Alert icon={<EditOutlined />} showIcon type='info' message={e.path} description={<><del>{JSON.stringify(e.oldValue)}</del> {JSON.stringify(e.value)}</>} /> : null}
                            {e.type === "ADD" ? <Alert icon={<PlusOutlined />} showIcon type='success' message={e.path} description={JSON.stringify(e.value)} /> : null}
                            {e.type === "REMOVE" ? <Alert icon={<DeleteOutlined />} showIcon type='error' message={e.path} description={<del>{JSON.stringify(e.value)}</del>} /> : null}
                        </>
                    )}
                </Col>
            </Row>
        </Modal>
    </>)
}

export default EditMaterial;